import React, { useMemo, useState } from "react";
import { Alert, Button, Empty, Input, Menu, Select, Table, Tag, Tooltip, message } from "antd";
import { EyeOutlined, MailOutlined, PlusOutlined, SearchOutlined, SwapOutlined } from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import { ListCard, Num, PageHeader, RecordLink, StatCards } from "components/shared-components/purchase";
import { CURRENCY } from "constants/DeliveryConstants";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import { formatPrice } from "functions/formatters";
import { PERMISSIONS } from "functions/permissions";
import {
  QUOTE_DISPLAY_STATUS,
  QUOTE_SEND_STATE,
  getQuoteDisplayStatus,
  getQuoteSendState,
  quoteMatchesSearch,
} from "functions/quotes";
import { useScopedQuotes } from "data/hooks/useQuotes";
import { usePermissions } from "data/hooks/usePermissions";
import urls from "urls";
import { QUOTE_STATUS_TAG, QuoteSendTag, customerLabel } from "./shared";
import SendQuoteModal from "./SendQuoteModal";
import ConvertQuoteModal from "./ConvertQuoteModal";
import { notifyConverted } from "./notifyConverted";

/**
 * Bestellingen → Offertes (BMX-58).
 *
 * Een offerte wordt gemaakt in de webshop: de verkoper gaat met de klant door de shop, vult het
 * mandje en kiest bij afrekenen "Opslaan als offerte". Hier komen ze samen. Standaard zie je de
 * lopende (open + verlopen); verlopen is geen eindstation, omzetten kan dan nog.
 */

const STATUS_FILTER_RUNNING = "running";
const STATUS_FILTER_ALL = "all";

const Quotes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.QUOTES_MANAGE);
  const { quotes, loading, error } = useScopedQuotes();

  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || STATUS_FILTER_RUNNING);
  const [salespersonFilter, setSalespersonFilter] = useState(null);
  const [sendQuote, setSendQuote] = useState(null);
  const [convertQuote, setConvertQuote] = useState(null);

  const withStatus = useMemo(
    () => quotes.map((quote) => ({ ...quote, displayStatus: getQuoteDisplayStatus(quote) })),
    [quotes],
  );

  const filtered = useMemo(
    () =>
      withStatus.filter((quote) => {
        if (statusFilter === STATUS_FILTER_RUNNING) {
          if (![QUOTE_DISPLAY_STATUS.OPEN, QUOTE_DISPLAY_STATUS.EXPIRED].includes(quote.displayStatus)) return false;
        } else if (statusFilter !== STATUS_FILTER_ALL && quote.displayStatus !== statusFilter) return false;
        if (salespersonFilter && quote.salesPersonEmail !== salespersonFilter) return false;
        return quoteMatchesSearch(quote, search);
      }),
    [withStatus, statusFilter, salespersonFilter, search],
  );

  const stats = useMemo(() => {
    const running = withStatus.filter((quote) =>
      [QUOTE_DISPLAY_STATUS.OPEN, QUOTE_DISPLAY_STATUS.EXPIRED].includes(quote.displayStatus),
    );
    return {
      open: running.filter((quote) => quote.displayStatus === QUOTE_DISPLAY_STATUS.OPEN).length,
      expired: running.filter((quote) => quote.displayStatus === QUOTE_DISPLAY_STATUS.EXPIRED).length,
      value: running.reduce((sum, quote) => sum + (quote.totalPrice || 0), 0),
      notSent: running.filter((quote) =>
        [QUOTE_SEND_STATE.NEVER, QUOTE_SEND_STATE.CHANGED, QUOTE_SEND_STATE.FAILED].includes(
          getQuoteSendState(quote).state,
        ),
      ).length,
    };
  }, [withStatus]);

  const salespersonOptions = useMemo(() => {
    const seen = new Map();
    quotes.forEach((quote) => {
      if (quote.salesPersonEmail && !seen.has(quote.salesPersonEmail)) {
        seen.set(quote.salesPersonEmail, quote.salesPersonName || quote.salesPersonEmail);
      }
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [quotes]);

  const updateParam = (key, value) =>
    setSearchParams(
      (params) => {
        if (!value) params.delete(key);
        else params.set(key, value);
        return params;
      },
      { replace: true },
    );

  const formatDate = (seconds) => (seconds ? dayjs.unix(seconds).format(DATE_FORMAT_DD_MM_YYYY) : "-");

  const formatDateTime = (seconds) => (seconds ? dayjs.unix(seconds).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-");

  const rowMenu = (quote) => {
    const open = [QUOTE_DISPLAY_STATUS.OPEN, QUOTE_DISPLAY_STATUS.EXPIRED].includes(quote.displayStatus);
    return (
      <Menu
        items={[
          {
            key: "details",
            icon: <EyeOutlined />,
            label: t("details"),
            onClick: () => navigate(urls.quoteDetail + quote.id),
          },
          canManage && open
            ? {
                key: "send",
                icon: <MailOutlined />,
                label: t("quote.actions.send"),
                onClick: () => setSendQuote(quote),
              }
            : null,
          canManage && open
            ? {
                key: "convert",
                icon: <SwapOutlined />,
                label: t("quote.actions.convert"),
                onClick: () => setConvertQuote(quote),
              }
            : null,
        ].filter(Boolean)}
      />
    );
  };

  const columns = [
    {
      title: t("quote.number"),
      dataIndex: "quoteNumber",
      width: 190,
      render: (value, quote) => (
        <>
          <RecordLink to={urls.quoteDetail + quote.id}>{value || quote.id}</RecordLink>
          {(quote.version || 1) > 1 ? (
            <Num muted size={12} style={{ marginLeft: 6 }}>
              v{quote.version}
            </Num>
          ) : null}
        </>
      ),
      sorter: (a, b) => (a.quoteNumber || "").localeCompare(b.quoteNumber || ""),
    },
    {
      title: t("customer.key"),
      key: "customer",
      render: (_, quote) => (
        <>
          <div>{customerLabel(quote.customer)}</div>
          {quote.customer?.company ? (
            <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
              {[quote.customer.firstName, quote.customer.lastName].filter(Boolean).join(" ")}
              {quote.isNewCustomer ? ` · ${t("quote.new_customer")}` : ""}
            </div>
          ) : null}
        </>
      ),
      sorter: (a, b) => customerLabel(a.customer).localeCompare(customerLabel(b.customer), "nl"),
    },
    {
      title: t("quote.created_at"),
      dataIndex: "createdAt",
      width: 120,
      render: formatDateTime,
      sorter: (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
      defaultSortOrder: "descend",
    },
    {
      title: t("quote.valid_until"),
      dataIndex: "validUntil",
      width: 120,
      render: (value, quote) => (
        <span style={quote.displayStatus === QUOTE_DISPLAY_STATUS.EXPIRED ? { color: "#d4380d" } : undefined}>
          {formatDate(value)}
        </span>
      ),
      sorter: (a, b) => (a.validUntil || 0) - (b.validUntil || 0),
    },
    {
      title: t("quote.sent"),
      key: "sent",
      width: 150,
      render: (_, quote) =>
        quote.displayStatus === QUOTE_DISPLAY_STATUS.CONVERTED ||
        quote.displayStatus === QUOTE_DISPLAY_STATUS.CANCELLED ? (
          <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>-</span>
        ) : (
          <QuoteSendTag quote={quote} />
        ),
    },
    {
      title: t("status"),
      dataIndex: "displayStatus",
      width: 130,
      render: (value, quote) => (
        <>
          <Tag color={QUOTE_STATUS_TAG[value]?.color}>{t(QUOTE_STATUS_TAG[value]?.key)}</Tag>
          {value === QUOTE_DISPLAY_STATUS.CONVERTED && quote.orderId ? (
            <div style={{ fontSize: 12 }}>
              <Num muted size={12}>
                {quote.orderId}
              </Num>
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: t("quote.salesperson"),
      dataIndex: "salesPersonName",
      width: 140,
      render: (value) => value || "-",
    },
    {
      title: t("quote.total_ex_vat"),
      dataIndex: "totalPrice",
      width: 120,
      align: "right",
      render: (value) => <Num align="right">{CURRENCY + formatPrice(value)}</Num>,
      sorter: (a, b) => (a.totalPrice || 0) - (b.totalPrice || 0),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, quote) => (
        <div className="text-right" onClick={(event) => event.stopPropagation()}>
          <EllipsisDropdown menu={rowMenu(quote)} />
        </div>
      ),
    },
  ];

  const subtitle = [
    t("quote.count_open", { count: stats.open }),
    t("quote.count_expired", { count: stats.expired }),
    t("quote.count_not_sent", { count: stats.notSent }),
  ].join(" · ");

  return (
    <>
      {contextHolder}
      <PageHeader
        title={t("quote.quotes")}
        subtitle={subtitle}
        extra={
          canManage && urls.webshopCheckout ? (
            <Tooltip title={t("quote.create_in_webshop_tooltip")}>
              <Button icon={<PlusOutlined />} href={urls.webshopCheckout} target="_blank" rel="noopener noreferrer">
                {t("quote.create_in_webshop")}
              </Button>
            </Tooltip>
          ) : null
        }
      />

      <StatCards
        items={[
          { label: t("quote.stats.open"), value: stats.open },
          {
            label: t("quote.stats.value"),
            value: CURRENCY + formatPrice(stats.value),
            hint: t("quote.stats.value_hint"),
          },
          { label: t("quote.stats.expired"), value: stats.expired, tone: stats.expired ? "warning" : undefined },
          { label: t("quote.stats.not_sent"), value: stats.notSent, tone: stats.notSent ? "warning" : undefined },
        ]}
      />

      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={t("quote.load_failed")} /> : null}

      <ListCard
        filters={
          <>
            <Input
              value={search}
              placeholder={t("quote.search_placeholder")}
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 280 }}
              onChange={(event) => {
                setSearch(event.target.value);
                updateParam("q", event.target.value);
              }}
            />
            <Select
              value={statusFilter}
              style={{ minWidth: 180 }}
              onChange={(value) => {
                setStatusFilter(value);
                updateParam("status", value === STATUS_FILTER_RUNNING ? null : value);
              }}
              options={[
                { value: STATUS_FILTER_RUNNING, label: t("quote.filter.running") },
                { value: QUOTE_DISPLAY_STATUS.OPEN, label: t("quote.status.open") },
                { value: QUOTE_DISPLAY_STATUS.EXPIRED, label: t("quote.status.expired") },
                { value: QUOTE_DISPLAY_STATUS.CONVERTED, label: t("quote.status.converted") },
                { value: QUOTE_DISPLAY_STATUS.CANCELLED, label: t("quote.status.cancelled") },
                { value: STATUS_FILTER_ALL, label: t("quote.filter.all") },
              ]}
            />
            {salespersonOptions.length > 1 ? (
              <Select
                allowClear
                placeholder={t("quote.salesperson")}
                value={salespersonFilter}
                style={{ minWidth: 180 }}
                options={salespersonOptions}
                onChange={(value) => setSalespersonFilter(value ?? null)}
              />
            ) : null}
          </>
        }
      >
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
            locale={{ emptyText: <Empty description={t("quote.empty")} /> }}
            onRow={(quote) => ({
              onClick: () => navigate(urls.quoteDetail + quote.id),
              style: { cursor: "pointer" },
            })}
          />
        </div>
      </ListCard>

      <SendQuoteModal
        quote={sendQuote}
        open={!!sendQuote}
        onClose={() => setSendQuote(null)}
        onSent={(result) => messageApi.success(t("quote.send.sent", { email: result.to }))}
      />
      <ConvertQuoteModal
        quote={convertQuote}
        open={!!convertQuote}
        onClose={() => setConvertQuote(null)}
        onConverted={(result) => notifyConverted(messageApi, t, result)}
      />
    </>
  );
};

export default Quotes;
